// Prompt attachments: which files the composer accepts, and how they are
// described to the workflow agent.
//
// A constants module rather than part of `pages/startProject.js` so that
// `DashboardComposer` and `components/PromptAttachments` can use it without
// pulling in react-router — v7's entry point does `require('react-router/dom')`,
// which the CRA jest resolver can't resolve, so any component that transitively
// imports it becomes unrenderable under test.
//
// Both extension sets are change-one-change-both mirrors of the module that
// CONSUMES the file. Widening one here without widening it there gets a file
// into the repository that nothing can read.

// Per-kind `accept` strings, so the composer's + menu can filter the picker to
// one kind. ATTACH_ACCEPT is COMPOSED from them rather than written out again —
// three literals listing the same extensions would be three things to widen.
/** @see classifyAttachment — these mirror CAD_EXT / GCODE_EXT below. */
export const CAD_ACCEPT = '.stp,.step,.igs,.iges';
export const GCODE_ACCEPT = '.gcode,.nc,.toolpath';

/** The composer's `accept` attribute. Cosmetic — a drag-drop bypasses it. */
export const ATTACH_ACCEPT = `${CAD_ACCEPT},${GCODE_ACCEPT}`;

// Mirrors CADInputPanel's repo-list filter + its own upload `accept`.
const CAD_EXT = /\.(stp|step|igs|iges)$/i;
// Mirrors AMInputPanel's uploaded-toolpath filter (`/\.(gcode|nc|txt|toolpath)$/i`)
// MINUS `.txt`: the AM picker tolerates it, but offering `.txt` here would invite
// notes and logs into the repository under the name of a toolpath.
const GCODE_EXT = /\.(gcode|nc|toolpath)$/i;

/**
 * 'cad' | 'gcode' | null — null means "nothing in this app can read it", which
 * is the composer's rejection signal.
 */
export function classifyAttachment(fileName) {
  const name = String(fileName || '');
  if (CAD_EXT.test(name)) return 'cad';
  if (GCODE_EXT.test(name)) return 'gcode';
  return null;
}

/**
 * The lines appended to the workflow agent's problem brief for a set of
 * attachments.
 *
 * The files are already in the user's repository by the time this runs, so the
 * agent's existing `list_available_files` / `set_cad_file` tools can act on them
 * — but only if it knows they exist, and the brief is the one channel that
 * survives into every later turn. Empty string when there is nothing to say, so
 * callers can `.filter(Boolean).join('\n\n')`.
 */
export function attachmentBriefLines(attachments) {
  const list = (attachments || []).filter((a) => a && a.fileName);
  if (!list.length) return '';
  const lines = list.map((a) => {
    const kind = a.fileType === 'gcode' ? 'G-code toolpath' : 'CAD geometry';
    return `- ${a.fileName} (${kind})`;
  });
  return `I have attached the following file${list.length > 1 ? 's' : ''}, already uploaded to my file repository:\n${lines.join('\n')}`;
}
